import * as React from 'react';

import styled from '@emotion/styled';
import { capitalize } from 'lodash';

import { Tab, TabContext, TabPanel, Tabs, Typography2 } from '@coursera/cds-core';
import { Box, Divider, Notification } from '@coursera/coursera-ui';

import SkillSetBadge from 'bundles/enterprise-admin-skills/components/SkillSetDetails/SkillSetBadge';
import { FAQCollapse } from 'bundles/enterprise-admin-skills/components/TSPFAQ';
import type { SkillMapping } from 'bundles/enterprise-admin-skills/components/types';
import { formatSkillName, proficiencyFilter } from 'bundles/enterprise-admin-skills/components/utils';
import { Spacer } from 'bundles/enterprise-ui/components/Spacer';
import { Heading } from 'bundles/enterprise-ui/components/typography';
import { unslugify } from 'bundles/enterprise-ui/utils';
import type { SkillSet as LearnerSkillSet } from 'bundles/naptimejs/resources/skillSets.v1';
import ProgressTracker from 'bundles/skills-common/private/progress-tracker/ProgressTracker';

import _t from 'i18n!nls/enterprise-admin-skills';

export const ModalContent = styled.div({
  width: '100%',
  maxWidth: '711px',
  margin: '0 auto',
});

type State = { tab: 'Skills' | 'FAQ' };
export class LearnerSkillSetDetails extends React.Component<
  { skillSet: LearnerSkillSet; skillMappings?: SkillMapping[] },
  State
> {
  state: State = {
    tab: 'Skills',
  };

  declare expandedFAQRef?: HTMLDivElement;

  renderSkills() {
    const { skillSet, skillMappings } = this.props;
    const { targetProficiencies = [] } = skillSet;
    return (
      <div data-e2e="learner-skillset-details-related-to-skills">
        <Spacer size={24} />
        <Notification
          message={
            <Typography2 component="p">
              {_t('These are the skills and proficiencies your learners will develop')}
            </Typography2>
          }
        />
        <Spacer size={32} />
        <Heading>{_t('Proficiency targets for this SkillSet:')}</Heading>
        <Spacer size={32} />
        {targetProficiencies.filter(proficiencyFilter).map((skill) => (
          <div key={skill.skillId}>
            <ProgressTracker
              skillName={formatSkillName(skill.skillId, skillMappings)}
              target={skill.targetProficiency}
              hideScore
            />
            <Spacer size={32} />
          </div>
        ))}
      </div>
    );
  }

  renderFaq() {
    return (
      <div data-e2e="learner-skillset-details-related-to-faq">
        <Spacer size={32} />
        <Heading>{_t('FAQ about SkillSets')}</Heading>
        <Spacer size={32} />
        <FAQCollapse />
      </div>
    );
  }

  render() {
    const { skillSet } = this.props;
    const { tab } = this.state;
    const title = skillSet.targetSkillProfileName;
    const description = skillSet.description;
    const goalType = capitalize(skillSet.lure?.toLowerCase());
    const academy = (skillSet.academyIds ?? []).map(unslugify).join(', ');
    return (
      <div data-e2e="learner-skillset-details">
        <SkillSetBadge
          style={{
            position: 'absolute',
            top: '24px',
            left: '24px',
          }}
        />
        <ModalContent>
          <Spacer size={32} />
          <Heading>{title}</Heading>
          <Spacer size={24} />
          <Box>
            <Typography2 component="strong">
              {_t('#{goalType} SkillSet', {
                goalType,
              })}
              {academy && (
                <React.Fragment>
                  &nbsp;-&nbsp;
                  {_t("featured in Coursera's #{academy} Academy", { academy })}
                </React.Fragment>
              )}
            </Typography2>
          </Box>
          <Spacer size={12} />
          <Divider />
          <Spacer size={12} />
          {description && (
            <div>
              <Typography2 component="p">{description}</Typography2>
            </div>
          )}
          <Spacer size={32} />
          <TabContext value={tab}>
            <Tabs
              onChange={(newTab: string) => {
                if (newTab === 'Skills' || newTab === 'FAQ') {
                  this.setState({ tab: newTab });
                }
              }}
            >
              <Tab label="Skills" value="Skills" />
              <Tab label="SkillSet FAQs" value="FAQ" />
            </Tabs>
            <TabPanel value="Skills">{this.renderSkills()}</TabPanel>
            <TabPanel value="FAQ">{this.renderFaq()}</TabPanel>
          </TabContext>
        </ModalContent>
      </div>
    );
  }
}
